import _getIterator from 'babel-runtime/core-js/get-iterator';
import _defineProperty from 'babel-runtime/helpers/defineProperty';
import _extends from 'babel-runtime/helpers/extends';
import _Object$assign from 'babel-runtime/core-js/object/assign';
import _toConsumableArray from 'babel-runtime/helpers/toConsumableArray';
import moment from 'moment';
import clone from 'lodash/clone';
import setWith from 'lodash/setWith';
import unset from 'lodash/unset';
import get from 'lodash/get';
import set from 'lodash/set';
import pick from 'lodash/pick';
import cloneDeep from 'lodash/cloneDeep';
import injected from '../injected';
import { reopened, pending, reviewed } from './reviewstatus';
import { computeUserFromSessionUser, areSameUser, cleanWatchers, getDisplayNames } from './user';
import { defaultStartTime } from './datetime';
import { pickRandomInArray, getObjectIndexInArray, getObjectIndexInArrayMatchingAtLeastOneField } from './array';
import { capitalize } from './string';
import translations from '../translations';

export var computeTaskBeforeCreate = function computeTaskBeforeCreate(task) {
  if (!task) {
    return task;
  }
  var fields = ['shouldBlockInCalendar', 'completed', 'description', 'estimatedTime', 'userId', 'email', 'when', 'title', 'to', 'tags', 'watchers', 'pin', 'dueDate', 'urlInfo', 'uploads', 'recurrencyDetails', 'reviewInputLink', 'multipleAssignees'];
  var computedTask = pick(task, fields);

  if (!get(task, 'to') && get(task, 'multipleAssignees')) {
    // in case task.multipleAssignees comes from a global state, we avoid modifying globally
    computedTask.multipleAssignees = [].concat(_toConsumableArray(computedTask.multipleAssignees));

    computedTask.multipleAssignees = computedTask.multipleAssignees.map(function (assignee) {
      if (assignee.userId && assignee.email) {
        delete assignee.email;
      }
      unset(assignee, 'picture');
      unset(assignee, 'displayName');
      return assignee;
    });
  } else {
    /* if no assign, we assign the task to connected user */
    if (!get(task, 'to.userId') && !get(task, 'to.email')) {
      set(computedTask, 'to.userId', injected.user.id);
    }

    // in case task.to comes from a global state, we avoid modifying globally
    computedTask.to = _Object$assign({}, computedTask.to);

    /* CREATE request require having userId OR email, not both */
    if (get(computedTask, 'to.userId') && get(computedTask, 'to.email')) {
      delete computedTask.to.email;
    }

    unset(computedTask, 'to.picture');
    unset(computedTask, 'to.displayName');
  }

  if (computedTask.watchers) {
    computedTask.watchers = cleanWatchers(computedTask.watchers);
  }

  if (computedTask.tags) {
    var tagsId = computedTask.tags.map(function (tag) {
      return tag.id;
    });
    computedTask.tagsId = tagsId;
  }

  return computedTask;
};

/*
 * return the task updated after a param has changed :
 * in some case, one or more params are related and also have to mutate
 */
export var computeTaskOnChange = function computeTaskOnChange(task, _ref, extraParams) {
  var paramName = _ref.paramName,
      value = _ref.value,
      method = _ref.method;

  var newTask = _Object$assign({}, task);
  var whenDate = get(task, 'when.date') ? moment(task.when.date) : null;

  switch (paramName) {
    case 'when.date':
      if (value) {
        setWith(newTask, 'when.date', value, clone);
        if (get(task, 'pin.time')) {
          whenDate = moment(value);
          /* we adjust pin.time date to copy when.date day/month/year */
          var pinTime = moment(task.pin.time).date(whenDate.date()).month(whenDate.month()).year(whenDate.year());
          setWith(newTask, 'pin.time', pinTime.format(), clone);
        } else if (get(task, 'pin.date')) {
          whenDate = moment(value);
          /* we update the pin.date */
          setWith(newTask, 'pin.date', whenDate.format('YYYY-MM-DD'), clone);
        }
      } else {
        unset(newTask, 'pin');
        unset(newTask, 'when');
        unset(newTask, 'shouldBlockInCalendar');
      }
      break;

    case 'pin.time':
      if (value) {
        /* if whenDate is null but pinTime is selected, define whenDate to today */
        if (!whenDate) {
          whenDate = moment();
          setWith(newTask, 'when.date', whenDate.format('YYYY-MM-DD'), clone);
        }
        /* if pin.time is changed, unset pin.date to override it */
        var _pinTime = moment(value).date(whenDate.date()).month(whenDate.month()).year(whenDate.year());
        unset(newTask, 'pin.date');
        setWith(newTask, 'pin.time', _pinTime.format(), clone);
        if (extraParams && extraParams.blockInCalendar) {
          setWith(newTask, 'shouldBlockInCalendar', true, clone);
        }
      } else {
        /* if pin.time is deleted, the pin.date toggle was activated so we set a pin.date to replace pin.time */
        unset(newTask, 'pin.time');
        setWith(newTask, 'pin.date', whenDate.format('YYYY-MM-DD'), clone);
        unset(newTask, 'shouldBlockInCalendar');
      }
      break;

    case 'pin.date':
      /* pin.date should only be true|false (switch task panel "It has to be done this day"), it then copy when.date */
      if (value) {
        /* if whenDate is null but pinDate is selected, define whenDate to today */
        if (!whenDate) {
          whenDate = moment();
          setWith(newTask, 'when.date', whenDate.format('YYYY-MM-DD'), clone);
        }
        setWith(newTask, 'pin.date', whenDate.format('YYYY-MM-DD'), clone);
      } else {
        unset(newTask, 'pin');
        unset(newTask, 'shouldBlockInCalendar');
      }
      break;
    case 'shouldBlockInCalendar':
      setWith(newTask, paramName, value, clone);
      if (value && !(task.pin && task.pin.time)) {
        if (!whenDate) {
          whenDate = moment();
          setWith(newTask, 'when.date', whenDate.format('YYYY-MM-DD'), clone);
        }
        var _pinTime2 = moment(defaultStartTime).date(whenDate.date()).month(whenDate.month()).year(whenDate.year());
        setWith(newTask, 'pin.time', _pinTime2.format(), clone);
      }

      break;

    case 'to':
      switch (method) {
        // only for multiple assignees
        case 'add':
          if (task.to) {
            var multipleAssignees = [task.to, value];
            unset(newTask, 'to');
            setWith(newTask, 'multipleAssignees', multipleAssignees, clone);
          } else if (task.multipleAssignees && task.multipleAssignees.length > 0) {
            var _multipleAssignees = cloneDeep(newTask.multipleAssignees);
            _multipleAssignees.push(value);
            setWith(newTask, 'multipleAssignees', _multipleAssignees, clone);
          } else {
            setWith(newTask, 'to', value, clone);
          }
          break;
        // only for multiple assignees
        case 'remove':
          if (task.to && areSameUser(task.to, value)) {
            unset(newTask, 'to');
          } else if (task.multipleAssignees && task.multipleAssignees.length === 2) {
            var index = getObjectIndexInArray(newTask.multipleAssignees, value, ['userId', 'email']);
            var to = index === 1 ? newTask.multipleAssignees[0] : newTask.multipleAssignees[1];
            unset(newTask, 'multipleAssignees');
            setWith(newTask, 'to', to, clone);
          } else {
            var _index = getObjectIndexInArray(newTask.multipleAssignees, value, ['userId', 'email']);
            var _multipleAssignees2 = cloneDeep(newTask.multipleAssignees);
            _multipleAssignees2.splice(_index, 1);
            setWith(newTask, 'multipleAssignees', _multipleAssignees2, clone);
          }
          break;
        // regular cases
        default:
          var oldAssignee = _Object$assign({}, task.to);
          var user = {
            userId: injected.user.id,
            picture: injected.user.picture,
            displayName: injected.user.displayName,
            isReviewer: true
          };
          if (value) {
            if (value.userId) {
              unset(newTask, 'to.email');
            } else if (value.email) {
              unset(newTask, 'to.userId');
            }
            setWith(newTask, 'to', value, clone);

            // add user as watcher/reviewer
            newTask = computeWatchers(newTask, user, 'add');
            // remove new assignee from watchers list
            newTask = computeWatchers(newTask, value, 'remove');
            // remove shouldBlockInCalendar
            unset(newTask, 'shouldBlockInCalendar');
          } else {
            setWith(newTask, 'to', null, clone);
            setWith(newTask, 'multipleAssignees', null, clone);
            // remove user from watchers list (as he is the new assignee)
            newTask = computeWatchers(newTask, user, 'remove');
          }

          // add oldAssignee as watcher if task already exists (ie if panel not in create mode)
          if (task.creationTime) {
            newTask = computeWatchers(newTask, oldAssignee, 'add');
          }
      }
      break;
    case 'watchers':
      newTask = computeWatchers(newTask, value, method);
      break;

    case 'comments':
      var comments = newTask.comments ? [].concat(newTask.comments) : [];
      if (method === 'add') {
        /* we set pending to true */
        value.pending = true;
        comments.unshift(value);
        setWith(newTask, paramName, comments, clone);
      } else if (method === 'remove') {
        /* we set pending to true on the comment to remove after the request */
        var _index2 = getObjectIndexInArray(comments, value);
        if (_index2 !== -1) {
          comments[_index2].pending = true;
          setWith(newTask, paramName, comments, clone);
        }
      }
      break;
    case 'uploads':
      var uploads = [].concat(newTask.uploads);
      uploads.push(value);
      setWith(newTask, paramName, uploads, clone);
      break;
    case 'reactions':
      var reactions = newTask.reactions ? [].concat(newTask.reactions) : [];
      if (method === 'add') {
        var newReaction = _extends({
          from: { userId: injected.user.id, picture: injected.user.picture, displayName: injected.user.displayName }
        }, value);
        reactions.unshift(newReaction);
        setWith(newTask, paramName, reactions, clone);
      } else if (method === 'remove') {
        var _index3 = getObjectIndexInArray(reactions, value, ['reaction', 'from.userId']);
        if (_index3 !== -1) {
          reactions.splice(_index3, 1);
          setWith(newTask, paramName, reactions, clone);
        }
      }
      break;
    case 'reviewStatus':
      newTask = computeWatchers(newTask, { userId: injected.user.id }, 'reviewStatus:' + value);
      if (value === reopened) {
        newTask.completed = 0;
      }
      break;
    case 'tags':
      var tags = newTask.tags ? [].concat(newTask.tags) : [];
      if (method === 'add') {
        var newTag = value;
        tags.push(newTag);
        setWith(newTask, paramName, tags, clone);
      } else if (method === 'remove') {
        var _index4 = getObjectIndexInArray(tags, value, ['id']);
        if (_index4 !== -1) {
          tags.splice(_index4, 1);
          setWith(newTask, paramName, tags, clone);
        }
      }
      break;
    default:
      setWith(newTask, paramName, value, clone);
      break;
  }
  return newTask;
};

var computeWatchers = function computeWatchers(task, value, method) {
  var newTask = _Object$assign({}, task);
  if (!value) {
    return newTask;
  }
  var watchers = newTask.watchers ? [].concat(newTask.watchers) : [];
  /* index of user in our watchers array */
  var index = getObjectIndexInArrayMatchingAtLeastOneField(watchers, value, ['userId', 'email']);

  if (method === 'add' && index === -1) {
    /* add regular watcher */
    watchers.push(value);
    setWith(newTask, 'watchers', watchers, clone);
  } else if (method === 'remove' && index !== -1) {
    /* remove regular watcher */
    watchers.splice(index, 1);
    setWith(newTask, 'watchers', watchers, clone);
  } else if (method === 'makeReviewer' && watchers[index]) {
    /* if user is already selected as a watcher, we add reviewer flag */
    watchers[index].isReviewer = true;
    setWith(newTask, 'watchers', watchers, clone);
  } else if (method === 'removeReviewer' && watchers[index]) {
    /* if user is already selected as a watcher, we remove reviewer flag */
    watchers[index].isReviewer = false;
    setWith(newTask, 'watchers', watchers, clone);
  } else if (method.indexOf('reviewStatus') !== -1 && index !== -1) {
    var reviewStatus = method.replace('reviewStatus:', '');
    watchers[index].reviewStatus = reviewStatus;
    setWith(newTask, 'watchers', watchers, clone);
  }
  return newTask;
};

/* compute the task with the patch success response */
export var computeTaskAfterPatchRequestSuccess = function computeTaskAfterPatchRequestSuccess(task, _ref2, response) {
  var paramName = _ref2.paramName,
      value = _ref2.value,
      _ref2$method = _ref2.method,
      method = _ref2$method === undefined ? 'change' : _ref2$method;

  if (!response) {
    return task;
  }

  switch (paramName) {
    case 'comments':
      if (method === 'add') {
        /* we add the final comment id and remove the pending property */
        if (!response.newCommentId) {
          break;
        }
        /* we create a new task object here to avoid mutating the object if no change is made */
        if (get(task, 'comments[0]') && !get(task, 'comments[0].id')) {
          task = _Object$assign({}, task);
          task.comments = [].concat(task.comments);
          task.comments[0] = _extends({}, task.comments[0], {
            id: response.newCommentId
          });
          delete task.comments[0].pending;
        }
      } else if (method === 'remove') {
        /* we completely remove the comment from the list */
        var index = getObjectIndexInArray(task.comments, value);
        if (index !== -1) {
          task = _Object$assign({}, task);
          task.comments = [].concat(task.comments);
          task.comments.splice(index, 1);
          setWith(task, paramName, task.comments, clone);
        }
      }
      break;
  }

  return task;
};

/* compute the task with the patch error response */
export var computeTaskAfterPatchRequestError = function computeTaskAfterPatchRequestError(task, _ref3, error) {
  var paramName = _ref3.paramName,
      value = _ref3.value,
      _ref3$method = _ref3.method,
      method = _ref3$method === undefined ? 'change' : _ref3$method;

  switch (paramName) {
    case 'comments':
      if (method === 'add') {
        /* remove the comment previously added */
        if (get(task, 'comments[0]') && !get(task, 'comments[0].id')) {
          task = _Object$assign({}, task);
          task.comments = [].concat(task.comments);
          task.comments.shift();
        }
      } else {
        /* unset pending property */
        var index = getObjectIndexInArray(task.comments, value);
        if (index !== -1) {
          task = _Object$assign({}, task);
          task.comments = [].concat(task.comments);
          task.comments[index] = _extends({}, task.comments[index]);
          delete task.comments[index].pending;
          setWith(task, paramName, task.comments, clone);
        }
      }
      break;
  }

  return task;
};

/*
 * In some case we change a param and need to send update another param
 * for example : updating when.date when pin.time is active require the app to update pin.time instead
 * this method will compute this and return the final PATCH body that need to be sent
 */
export var computePatchRequest = function computePatchRequest(task, _ref4) {
  var paramName = _ref4.paramName,
      value = _ref4.value,
      _ref4$method = _ref4.method,
      method = _ref4$method === undefined ? 'change' : _ref4$method;
  var extraParams = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

  var params = _defineProperty({}, paramName, value);
  var body = {};

  try {
    /* if param is a date, we get the value formatted as YYYY-MM-DD */
    var whenDate = get(task, 'when.date') ? moment(task.when.date) : null;

    var dateFields = ['when.date', 'dueDate'];
    if (dateFields.indexOf(paramName) !== -1) {
      var date = value ? moment(value) : null;
      value = date ? date.format('YYYY-MM-DD') : '';
    }

    /* if when.date is updated and task have a pinTime, we update the pinTime value */
    if (date && paramName === 'when.date' && get(task, 'pin.time')) {
      var pinTime = moment(task.pin.time);
      pinTime.date(date.date()).month(date.month()).year(date.year());
      params = { 'pin.time': pinTime.format() };
    }

    /* if when.date is updated and pin.date is active */
    if (date && paramName === 'when.date' && get(task, 'pin.date')) {
      params = { 'pin.date': value };
    }

    if (paramName === 'pin.time' && whenDate && value) {
      /* if pin.time is changed, we take the when.date and change the hour */
      var _pinTime3 = moment(value);

      _pinTime3.date(whenDate.date()).month(whenDate.month()).year(whenDate.year());
      params = { 'pin.time': _pinTime3.format() };
      body = { shouldBlockInCalendar: extraParams.blockInCalendar };
      delete extraParams.blockInCalendar;
    } else if (paramName === 'pin.time' && whenDate && !value) {
      /* if pin.time is deactivated, we patch the pin.date */
      params = { 'pin.date': whenDate.format('YYYY-MM-DD') };
    } else if (paramName === 'pin.time' && !whenDate) {
      params = { pin: null };
    }

    /* if pin.date === true : we take the when.date, else : we send null */
    if (paramName === 'pin.date' && whenDate && value) {
      params = { 'pin.date': whenDate.format('YYYY-MM-DD') };
    } else if (paramName === 'pin.date' && !value) {
      params = { pin: null };
    } else if (paramName === 'pin.date' && !whenDate) {
      params = { pin: null };
    }

    if (paramName === 'to') {
      params = { assignee: 'change' };
      if (value && value.userId) {
        body = { userId: value.userId };
      } else if (value && value.email) {
        body = { email: value.email };
      } else {
        body = { userId: injected.user.id };
      }
    }

    if (paramName === 'watchers') {
      params = { watchers: method };
      if (value) {
        body = value;
        body = body.userId ? { userId: body.userId, isReviewer: !!body.isReviewer } : { email: body.email, isReviewer: !!body.isReviewer };
      }
    }

    if (paramName === 'comments') {
      params = { comments: method };
      if (method === 'add') {
        body = { richText: value.richText };
      } else if (method === 'remove') {
        body = { id: value.id };
      } else if (method === 'edit') {
        body = { id: value.id, richText: value.richText };
      }
    }

    if (paramName === 'uploads') {
      params = { uploads: method };
      if (method === 'add') {
        body = value;
      } else {
        throw new Error('Unsupported method for upload PATCH : ' + method);
      }
    }

    if (paramName === 'recurrencyDetails') {
      params = { recurrencyDetails: method };
      if (method !== 'remove') {
        body = value;
      }
    }

    if (paramName === 'reactions') {
      params = { reactions: method };
      body = { reaction: value.reaction };
    }

    if (paramName === 'tags') {
      params = { tags: method };
      body = value;
    }

    if (paramName === 'shouldBlockInCalendar') {
      if (value) {
        if (!(task.pin && task.pin.time)) {
          throw new Error('The task should be pinned to a time');
        }
        body = { time: task.pin.time };
      }
    }
  } catch (err) {
    console.error(err);
  }

  params = _extends({}, params, extraParams);

  return { params: params, body: body };
};

export var isReviewTask = function isReviewTask(task) {
  return !!task.reviewedTaskId;
};

/* get user specific informations about a regular task */
export var getTaskDataForUser = function getTaskDataForUser(task, user) {
  var data = {
    /* user dependant informations */
    isAssignee: user && task.to && task.to.userId === user.id,
    isWatcher: false,
    isReviewer: false,
    reviewStatus: null,

    /* user independant informations */
    isReviewTask: !!task.reviewedTaskId,
    hasReviewer: false,
    hasReviewerWithStatusPending: false,
    hasReviewerWithStatusReopened: false,
    isReviewedByAllReviewers: true
  };
  if (task.watchers) {
    var _iteratorNormalCompletion = true;
    var _didIteratorError = false;
    var _iteratorError = undefined;

    try {
      for (var _iterator = _getIterator(task.watchers), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
        var watcher = _step.value;

        if (watcher.isReviewer) {
          data.hasReviewer = true;
        }
        if (watcher.isReviewer && watcher.reviewStatus !== reviewed) {
          data.isReviewedByAllReviewers = false;
        }
        if (watcher.isReviewer && watcher.reviewStatus === pending) {
          data.hasReviewerWithStatusPending = true;
        }
        if (watcher.isReviewer && watcher.reviewStatus === reopened) {
          data.hasReviewerWithStatusReopened = true;
        }
        if (watcher.userId && user && watcher.userId === user.id) {
          data.isWatcher = true;
          data.isReviewer = watcher.isReviewer;
          data.reviewStatus = watcher.reviewStatus;
        }
      }
    } catch (err) {
      _didIteratorError = true;
      _iteratorError = err;
    } finally {
      try {
        if (!_iteratorNormalCompletion && _iterator.return) {
          _iterator.return();
        }
      } finally {
        if (_didIteratorError) {
          throw _iteratorError;
        }
      }
    }
  }

  return data;
};

export var getReviewers = function getReviewers(task, sessionUser) {
  var reviewers = [];

  var user = computeUserFromSessionUser(sessionUser);

  if (task.watchers) {
    var _iteratorNormalCompletion2 = true;
    var _didIteratorError2 = false;
    var _iteratorError2 = undefined;

    try {
      for (var _iterator2 = _getIterator(task.watchers), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
        var watcher = _step2.value;

        if (watcher.isReviewer && !areSameUser(user, watcher)) {
          reviewers.push(watcher);
        }
      }
    } catch (err) {
      _didIteratorError2 = true;
      _iteratorError2 = err;
    } finally {
      try {
        if (!_iteratorNormalCompletion2 && _iterator2.return) {
          _iterator2.return();
        }
      } finally {
        if (_didIteratorError2) {
          throw _iteratorError2;
        }
      }
    }
  }

  return reviewers;
};

export var getRandomCompletionMessage = function getRandomCompletionMessage(task, agent) {
  var reviewers = getReviewers(task, agent);

  if (reviewers.length > 0) {
    var randomMessage = pickRandomInArray(translations.task.completed.reviewMessages);
    var displayNames = getDisplayNames(reviewers);
    return capitalize(randomMessage.replace('[ReviewerDisplayNames]', displayNames));
  } else {
    var _randomMessage = pickRandomInArray(translations.task.completed.messages);
    var displayName = agent.displayName || '';
    return capitalize(_randomMessage.replace('[displayName]', displayName));
  }
};
//# sourceMappingURL=task.js.map


//////////////////
// WEBPACK FOOTER
// ../gipsy-misc/lib/utils/task.js
// module id = 391
// module chunks = 0